import { placeKey } from '@/core/rng';
import { CHUNK_SIZE } from './chunk';

/**
 * Things a god has picked up and put down somewhere else — Design IV §21–§27.
 *
 * *The player should be able to touch the world.* Not through an editor and
 * not through a `moveTo`: you take hold of a real object, it resists, it comes
 * free or it tears, and **the simulation continues from wherever it ends up**.
 * A log dropped across a creek is a crossing; animals use it, mortals find it,
 * a path forms, and in three hundred years the ford may be the reason a town
 * is there. None of that is scripted, and none of it needed a *Build Bridge*
 * verb. That is the whole argument for Grasp: **physical manipulation creates
 * causes; the simulation creates consequences.**
 *
 * ## Why this is a store and not a property
 *
 * Baseline world content is a pure function of position and seed — a stone's
 * position *is* its name. So an object that has been *moved* is, precisely,
 * divergence from that function, and belongs in sparse history exactly like
 * `world/things.ts` and for exactly the same reason. An empty store costs
 * nothing and the world is what the seed says.
 *
 * The key is the object's **origin** — where the baseline put it — and never
 * where it is now. That is what makes the record stable: a thing moved twice
 * has one entry, not two, and the question "is the stone still in cell K?"
 * stays a single lookup however far it has travelled.
 */

/** One object that is no longer where the world grew it. */
export interface CarriedThing {
  /** `placeKey` of where the baseline put it — its identity, and the key. */
  key: string;
  /** What it is, in whatever vocabulary its own layer uses. */
  kind: string;
  /** Where it is *now*, world metres. */
  x: number; z: number;
  /** Its own footprint in metres — the renderer draws it at its real size. */
  size: number;
  /** World time it was last put down. */
  at: number;
  /** 0..1, how roughly it has been handled. Living things mind; stones do not. */
  damage: number;
  /** How many times a god has picked this particular thing up. */
  lifts: number;
}

export interface CarriedSnapshot { things: CarriedThing[] }

const chunkOf = (x: number, z: number) =>
  `${Math.floor(x / CHUNK_SIZE)},${Math.floor(z / CHUNK_SIZE)}`;

export class CarriedStore {
  private readonly moved = new Map<string, CarriedThing>();
  private version_ = 0;

  /**
   * Take the thing whose home is `key` and put it down at (x, z).
   *
   * Returns the chunk keys whose appearance changed — both of them, when the
   * thing crossed a chunk boundary, because the ground it left has to forget
   * it as surely as the ground it landed on has to show it.
   */
  place(key: string, kind: string, size: number, homeX: number, homeZ: number,
        x: number, z: number, at: number, damage: number): string[] {
    const prev = this.moved.get(key);
    const dirty = new Set<string>();
    dirty.add(chunkOf(prev ? prev.x : homeX, prev ? prev.z : homeZ));
    dirty.add(chunkOf(x, z));
    this.moved.set(key, {
      key, kind, size, x, z, at,
      // handling accumulates: a thing dragged about repeatedly is the worse
      // for it, and cannot be un-damaged by being put back
      damage: Math.min(1, (prev?.damage ?? 0) + damage),
      lifts: (prev?.lifts ?? 0) + 1,
    });
    this.version_++;
    return [...dirty];
  }

  /** Where this thing is now, or null if it never moved. */
  at(key: string): CarriedThing | null {
    return this.moved.get(key) ?? null;
  }

  /**
   * Has the thing that belongs at this place been taken away from it?
   *
   * This is the question the *baseline* layers have to ask before they draw
   * anything: `world/litter.ts` grows a stone at every occupied cell for ever,
   * and once a god has carried one off, that cell is empty. The same shape of
   * question `sparing()` asks for promotion — one yes/no about a place.
   */
  displaced(x: number, z: number): boolean {
    return this.moved.has(placeKey(x, z));
  }

  /** Everything now lying in one chunk that the baseline did not put there. */
  inChunk(cx: number, cz: number): CarriedThing[] {
    const want = `${cx},${cz}`;
    const out: CarriedThing[] = [];
    for (const t of this.moved.values()) if (chunkOf(t.x, t.z) === want) out.push(t);
    return out;
  }

  /**
   * Everything lying within `r` metres of a point. What the renderer wants:
   * asking by chunk drops a thing the moment it is carried over a boundary,
   * and a stone that vanishes because it crossed an invisible line is worse
   * than one that was never drawn.
   */
  near(x: number, z: number, r: number): CarriedThing[] {
    const out: CarriedThing[] = [];
    const r2 = r * r;
    for (const t of this.moved.values()) {
      if ((t.x - x) ** 2 + (t.z - z) ** 2 <= r2) out.push(t);
    }
    return out;
  }

  /** Everything a god has moved, wherever it now is. Read-only, and the only
   *  caller is the renderer asking which relics are no longer where they grew. */
  all(): readonly CarriedThing[] { return [...this.moved.values()]; }

  get size(): number { return this.moved.size; }
  /** A cache tag, not history — the same contract as `EnvField.version`. */
  get version(): number { return this.version_; }

  toSnapshot(): CarriedSnapshot {
    return { things: [...this.moved.values()].map((t) => ({ ...t })) };
  }

  static fromSnapshot(s: CarriedSnapshot | undefined): CarriedStore {
    const store = new CarriedStore();
    store.restore(s);
    return store;
  }

  /**
   * Replace the contents wholesale. A copy, never a replay: routing a restore
   * back through `place()` would count every thing as lifted again and add its
   * handling damage a second time, so a world would come back from a save
   * slightly more battered than it went in.
   */
  restore(s: CarriedSnapshot | undefined): void {
    this.moved.clear();
    for (const t of s?.things ?? []) this.moved.set(t.key, { ...t });
    this.version_++;
  }
}

/**
 * The predicate the baseline layers take, or `undefined` where nothing has
 * ever been moved — so an untouched world does not allocate a closure, let
 * alone ask a question per pebble. Same bargain as `sparing()`.
 */
export const displacing = (c: CarriedStore): ((x: number, z: number) => boolean) | undefined =>
  c.size === 0 ? undefined : (x, z) => c.displaced(x, z);
